import notificationApi from '../api/notifications';

/**
 * Convert a base64url-encoded VAPID application server key (as served by the
 * backend config endpoint) into the Uint8Array PushManager.subscribe expects.
 */
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

const isSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/**
 * Overall push state for the UI (bell icon / Live Notify button):
 * - supported: browser can do web push at all
 * - enabled:   the backend master switch is ON
 * - permission: 'granted' | 'denied' | 'default'
 * - subscribed: a live PushSubscription exists for this browser
 */
export const getPushState = async () => {
  if (!isSupported()) {
    return { supported: false, enabled: false, permission: 'unsupported', subscribed: false };
  }
  let config = null;
  try {
    config = await notificationApi.getConfig();
  } catch (err) {
    config = { enabled: true, vapid_public_key: null };
  }
  let registration = null;
  try {
    registration = await navigator.serviceWorker.getRegistration();
  } catch (err) {
    registration = null;
  }
  let subscription = null;
  if (registration) {
    try {
      subscription = await registration.pushManager.getSubscription();
    } catch (err) {
      subscription = null;
    }
  }
  return {
    supported: true,
    enabled: Boolean(config.enabled),
    vapidKey: config.vapid_public_key || null,
    permission: Notification.permission,
    subscribed: Boolean(subscription),
    subscription,
    registration,
  };
};

/** Ask the user for permission and subscribe this browser to web push. */
export const subscribeToPush = async (parivarId = null) => {
  let state = await getPushState();
  if (!state.supported || !state.enabled) {
    return state;
  }

  if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked in your browser. Allow them in site settings and try again.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  let registration = state.registration;
  if (!registration) {
    registration = await navigator.serviceWorker.ready;
  }
  if (!state.vapidKey) {
    throw new Error('Push is not configured yet. Please try again shortly.');
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(state.vapidKey),
  });
  await notificationApi.registerSubscription(subscription, parivarId);
  return getPushState();
};

/** Remove this browser's push subscription (best-effort server cleanup). */
export const unsubscribeFromPush = async () => {
  const state = await getPushState();
  if (state.subscription) {
    const endpoint = state.subscription.endpoint;
    try {
      await state.subscription.unsubscribe();
    } catch (err) {
      // Ignore: the local unsubscribe may already be gone.
    }
    try {
      await notificationApi.removeSubscription(endpoint);
    } catch (err) {
      // Ignore: server row may already be removed.
    }
  }
  return getPushState();
};

/** Persist a freshly-created subscription (e.g. after pushsubscriptionchange). */
export const syncPushSubscription = async (parivarId = null) => {
  const reg = await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  if (subscription) {
    await notificationApi.registerSubscription(subscription, parivarId);
  }
};

/**
 * Show a notification when a live darshan starts — only if the user has
 * granted permission and opted in via the Live page "Notify Me" flow.
 */
export const notifyLiveStarted = async ({ title = 'Live Darshan is live now', body = 'The live darshan has begun — watch now on the Live page.', url = '/live' } = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false;
  }
  const reg = await navigator.serviceWorker.ready;
  const options = {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    data: { url },
    vibrate: [100, 50, 100],
    // Keep sw.js source entirely in-sync: reuse the plain Notification API via
    // the registration so behavior matches pushed notifications.
  };
  await reg.showNotification(title, options);
  return true;
};